import { LitElement, html, css } from 'lit'
import { api } from '../api.js'
import { toast } from './app-toast.js'
import { runTicketAction, WmjCancelled } from '../wmj-flow.js'
import './rich-editor.js'

// Reply composer. All users post public replies; agents get an internal-note
// toggle. Uses <rich-editor> for formatting + inline images + attachments.
// Dispatches `ticket-changed` so the parent refetches the thread.
class TicketReply extends LitElement {
  static properties = {
    number: { type: Number },
    status: { type: String },
    isAgent: { type: Boolean },
    client: { type: Object },
    _internal: { state: true },
    _busy: { state: true }
  }

  static styles = css`
    :host { display: block; font-family: var(--font-sans); }
    .box { background: var(--white); border-radius: var(--radius-card); padding: 24px; box-shadow: var(--shadow-row);
           border-top: 3px solid var(--eggplant-500); }
    .box.internal { background: var(--chartreuse-bg); border-top-color: var(--chartreuse-500); }
    .eyebrow { display: flex; flex-direction: column; gap: 8px; align-items: flex-start; margin-bottom: 14px; }
    .eyebrow span { font-family: var(--font-eyebrow); font-weight: 700; font-size: 12px; line-height: 1.5;
                    letter-spacing: 0.12em; text-transform: uppercase; color: var(--eggplant-500); }
    .eyebrow i { width: 20px; height: 2px; background: var(--chartreuse-500); display: block; }
    .editor { box-shadow: inset 0 0 0 1px var(--border-input); border-radius: 5px; overflow: hidden; background: var(--white); }
    .actions { display: flex; align-items: center; gap: 16px; margin-top: 14px; }
    .toggle { display: inline-flex; gap: 8px; align-items: center; font-size: 13px; color: var(--neutral-600);
              cursor: pointer; user-select: none; }
    .toggle input { width: 16px; height: 16px; accent-color: var(--eggplant-500); }
    button.send { display: inline-flex; align-items: center; gap: 8px; margin-left: auto; cursor: pointer;
             font-family: var(--font-sans); font-weight: 600; font-size: 16px; line-height: 24px;
             padding: 12px 24px; border: none; border-radius: 0; transition: background-color .18s ease, color .18s ease; }
    button.send.public { background: var(--chartreuse-500); color: var(--eggplant-500); }
    button.send.public:hover { background: var(--chartreuse-600); }
    button.send.note { background: var(--eggplant-500); color: var(--neutral-50); }
    button.send.note:hover { background: var(--eggplant-700); }
    button.send:disabled { opacity: 0.4; cursor: not-allowed; }
    .arrow { font-size: 16px; line-height: 0; }
    .closed { color: var(--neutral-600); font-size: 15px; padding: 8px; }
  `

  constructor() {
    super()
    this._internal = false
    this._busy = false
  }

  get _editor() { return this.renderRoot.querySelector('rich-editor') }

  async _submit() {
    const editor = this._editor
    if (!editor || editor.isEmpty()) { toast('Write something first', 'info'); return }
    const { html: body, attachmentIds } = editor.getContent()
    this._busy = true
    const path = this._internal ? `/tickets/${this.number}/notes` : `/tickets/${this.number}/comments`
    const post = () => api.post(path, { body, attachmentIds })
    try {
      // Agent posts are first-touch-enforced (§18.3) and route through the picker
      // flow; client replies post directly (never blocked).
      if (this.isAgent) {
        await runTicketAction(post, { ticketNumber: this.number, client: this.client })
      } else {
        await post()
      }
      editor.clear()
      this.dispatchEvent(new CustomEvent('ticket-changed', { bubbles: true, composed: true }))
    } catch (err) {
      // Cancelled linking: keep the draft, but refresh so any company/job linked
      // mid-flow is reflected in the sidebar.
      if (err instanceof WmjCancelled) {
        this.dispatchEvent(new CustomEvent('ticket-changed', { bubbles: true, composed: true }))
      } else {
        toast(err.message || 'Failed to post', 'error')
      }
    } finally {
      this._busy = false
    }
  }

  render() {
    if (this.status === 'closed') {
      return html`<div class="box"><div class="closed">This ticket is closed. Open a new ticket to continue.</div></div>`
    }
    return html`
      <div class="box ${this._internal ? 'internal' : ''}">
        <div class="eyebrow"><span>${this._internal ? 'Internal note' : 'Reply to requester'}</span><i></i></div>
        <div class="editor">
          <rich-editor .ticketNumber=${this.number}
            .placeholder=${this._internal ? 'Add a private note for the team…' : 'Write a reply…'}></rich-editor>
        </div>
        <div class="actions">
          ${this.isAgent ? html`
            <label class="toggle">
              <input type="checkbox" .checked=${this._internal} @change=${e => (this._internal = e.target.checked)} />
              Internal note
            </label>` : ''}
          <button class="send ${this._internal ? 'note' : 'public'}" ?disabled=${this._busy} @click=${this._submit}>
            ${this._busy ? 'Posting…' : html`${this._internal ? 'Save note' : 'Send reply'} <span class="arrow">→</span>`}
          </button>
        </div>
      </div>
    `
  }
}

customElements.define('ticket-reply', TicketReply)
